import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CreditCard } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';

interface Props {
  children: React.ReactNode;
}

export default function SubscriptionGate({ children }: Props) {
  const { currentOrg, isBoardMember } = useOrganization();
  const location = useLocation();
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();

  const isOnBillingPage = location.pathname.endsWith('/billing');
  const status = currentOrg?.subscriptionStatus ?? 'trial';
  const isBlocked = (status === 'expired' || status === 'payment_failed') && !isOnBillingPage;

  if (!isBlocked) return <>{children}</>;

  const isPaymentFailed = status === 'payment_failed';

  return (
    <div className="relative flex-1">
      <div className="pointer-events-none opacity-20 select-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm">
        <div className="max-w-md w-full mx-4 bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            isPaymentFailed ? 'bg-orange-100' : 'bg-red-100'
          }`}>
            <AlertTriangle className={`w-8 h-8 ${isPaymentFailed ? 'text-orange-600' : 'text-red-600'}`} />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {isPaymentFailed ? 'Betalningsproblem' : 'Prenumeration saknas'}
          </h2>
          <p className="text-gray-600 text-sm mb-6">
            {isPaymentFailed
              ? 'Er betalning verkar inte gått igenom, vänligen se över era kreditkorts uppgifter.'
              : 'Er prenumeration har löpt ut. Aktivera en prenumeration för att fortsätta använda tjänsten.'}
          </p>
          {isBoardMember && (
            <button
              onClick={() => navigate(`/org/${orgId}/billing`)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              <CreditCard className="w-5 h-5" />
              Gå till Fakturering
            </button>
          )}
          {!isBoardMember && (
            <p className="text-sm text-gray-500">
              Kontakta er styrelsemedlem för att åtgärda detta.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
